import { clamp, preventDefault } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/** Click / double-tap zoom level (Fancybox-style toggleZoom) */
const CLICK_ZOOM = 2;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 8;
const WHEEL_ZOOM_STEP = 0.12;

/**
 * Fancybox-style pan/zoom wrapper for lightbox images.
 * - Click / double-tap: toggle zoom (fit ↔ 2x), centered on pointer
 * - Wheel: zoom toward cursor
 * - Mouse move while zoomed: pan (panMode: mousemove)
 * - Drag while zoomed: pan (pointer / touch)
 * - Pinch: continuous zoom on touch devices
 *
 * @extends {HTMLElement}
 */
export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;
  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;
  /** @type {boolean} */
  #suppressClick = false;
  /** @type {number} */
  #lastTouchEndTime = 0;
  /** @type {boolean} */
  #pointerActive = false;
  /** @type {number | null} */
  #activePointerId = null;
  /** @type {'mousemove' | 'drag'} */
  #panMode = 'mousemove';

  get #image() {
    return this.querySelector('img');
  }

  /** Whether the image is currently zoomed past fit */
  get isZoomed() {
    return this.#scale > MIN_ZOOM + 0.01;
  }

  /** Current scale factor */
  get scale() {
    return this.#scale;
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#initEventListeners();
    this.#updateTransform();
    this.#syncZoomAttribute();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('touchcancel', this.#handleTouchEnd, options);

    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointermove', this.#handlePointerMove, options);
    this.addEventListener('pointerup', this.#handlePointerUp, options);
    this.addEventListener('pointercancel', this.#handlePointerUp, options);
    this.addEventListener('click', this.#handleClick, options);
    this.addEventListener('wheel', this.#handleWheel, options);
    this.addEventListener('dblclick', this.#handleDoubleClick, options);

    this.#updateTransform();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
      this.#panMode = 'drag';
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    // When zoomed, claim the gesture for panning (block slide swipe)
    if (this.isZoomed) {
      preventDefault(event);
    }

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.#lastTapPosition) {
      const distance = getDistance(touch, this.#lastTapPosition);

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#toggleZoomAtPoint(touch.clientX, touch.clientY);
        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);
    this.#startDragGestureFromPoint(touch.clientX, touch.clientY);
    this.#panMode = 'drag';
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {number} x
   * @param {number} y
   */
  #startDragGestureFromPoint(x, y) {
    this.#startPosition = { x, y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch | Point} point
   */
  #storeTapInfo(currentTime, point) {
    this.#lastTapTime = currentTime;
    const x = 'clientX' in point ? point.clientX : point.x;
    const y = 'clientY' in point ? point.clientY : point.y;
    this.#lastTapPosition = { x, y };
  }

  /**
   * Fancybox-style toggleZoom toward a point
   * @param {number} clientX
   * @param {number} clientY
   */
  #toggleZoomAtPoint(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    const containerCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    if (this.isZoomed) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    } else {
      const oldScale = this.#scale;
      this.#scale = CLICK_ZOOM;

      const distanceFromCenter = {
        x: clientX - containerCenter.x,
        y: clientY - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    }

    this.#requestUpdateTransform();
    this.#syncZoomAttribute();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    // Only pan when zoomed; otherwise let the gallery scroll horizontally
    if (touchCount === 1 && this.#isDragging && this.isZoomed) {
      preventDefault(event);
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch.clientX, touch.clientY);
      }
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;

    this.#scale = clamp((currentDistance / this.#initialDistance) * this.#startScale, MIN_ZOOM, MAX_ZOOM);

    const rect = this.getBoundingClientRect();
    const containerCenterX = rect.left + rect.width / 2;
    const containerCenterY = rect.top + rect.height / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#syncZoomAttribute();
    this.#isDragging = false;
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #processDragGesture(clientX, clientY) {
    const distance = getDistance({ x: clientX, y: clientY }, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;
    this.#panMode = 'drag';

    const dx = clientX - this.#startPosition.x;
    const dy = clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    this.#lastTouchEndTime = performance.now();

    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#hasDraggedBeyondThreshold = false;
      this.#requestUpdateTransform();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    this.#pointerActive = true;
    this.#activePointerId = event.pointerId;
    this.#startDragGestureFromPoint(event.clientX, event.clientY);
    this.#panMode = this.isZoomed ? 'drag' : 'mousemove';

    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture can fail if the element is removed mid-gesture
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;

    // Fancybox panMode: mousemove — pan while zoomed without requiring a drag
    if (this.isZoomed && !this.#pointerActive && this.#panMode === 'mousemove') {
      this.#panToPointer(event.clientX, event.clientY);
      return;
    }

    if (!this.#pointerActive || event.pointerId !== this.#activePointerId) return;

    if (this.isZoomed) {
      this.#processDragGesture(event.clientX, event.clientY);
    } else {
      const distance = getDistance({ x: event.clientX, y: event.clientY }, this.#startPosition);
      if (distance >= DRAG_THRESHOLD) {
        this.#hasDraggedBeyondThreshold = true;
      }
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerId !== this.#activePointerId && this.#activePointerId !== null) return;

    this.#suppressClick = this.#hasDraggedBeyondThreshold;
    this.#pointerActive = false;
    this.#activePointerId = null;
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.#panMode = 'mousemove';

    try {
      this.releasePointerCapture(event.pointerId);
    } catch {
      // Already released
    }
  };

  /**
   * Fancybox contentClick: toggleZoom on primary click (desktop).
   * Touch uses double-tap instead so single-finger swipe can change slides.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    // Ignore synthetic clicks from touch (mobile uses double-tap to zoom)
    if (performance.now() - this.#lastTouchEndTime < 450) return;

    if (this.#suppressClick) {
      this.#suppressClick = false;
      return;
    }

    // Ignore 2nd+ click in a double/triple-click sequence to avoid flicker
    if (event.detail !== 1) return;

    this.#toggleZoomAtPoint(event.clientX, event.clientY);
    this.#panMode = 'mousemove';
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDoubleClick = (event) => {
    preventDefault(event);
  };

  /**
   * Map pointer position to pan offset (Fancybox panMode: mousemove)
   * @param {number} clientX
   * @param {number} clientY
   */
  #panToPointer(clientX, clientY) {
    const bounds = this.#getPanBounds();
    if (!bounds || !this.isZoomed) return;

    const rect = this.getBoundingClientRect();
    const relX = clamp((clientX - rect.left) / rect.width, 0, 1);
    const relY = clamp((clientY - rect.top) / rect.height, 0, 1);

    this.#translate.x = (0.5 - relX) * 2 * bounds.maxTranslateX;
    this.#translate.y = (0.5 - relY) * 2 * bounds.maxTranslateY;

    this.#requestUpdateTransform();
  }

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    preventDefault(event);

    const oldScale = this.#scale;
    const direction = event.deltaY < 0 ? 1 : -1;
    const nextScale = clamp(oldScale * (1 + direction * WHEEL_ZOOM_STEP), MIN_ZOOM, MAX_ZOOM);

    if (nextScale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const containerCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    const distanceFromCenter = {
      x: event.clientX - containerCenter.x,
      y: event.clientY - containerCenter.y,
    };

    this.#scale = nextScale;
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
    }

    this.#panMode = 'mousemove';
    this.#requestUpdateTransform();
    this.#syncZoomAttribute();
  };

  /**
   * Programmatic zoom in (toolbar)
   */
  zoomIn() {
    const rect = this.getBoundingClientRect();
    this.#zoomBy(1 + WHEEL_ZOOM_STEP * 2, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  /**
   * Programmatic zoom out (toolbar)
   */
  zoomOut() {
    const rect = this.getBoundingClientRect();
    this.#zoomBy(1 / (1 + WHEEL_ZOOM_STEP * 2), rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  /**
   * @param {number} factor
   * @param {number} clientX
   * @param {number} clientY
   */
  #zoomBy(factor, clientX, clientY) {
    const oldScale = this.#scale;
    this.#scale = clamp(oldScale * factor, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const containerCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    const distanceFromCenter = {
      x: clientX - containerCenter.x,
      y: clientY - containerCenter.y,
    };
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
    this.#syncZoomAttribute();
  }

  #syncZoomAttribute() {
    this.toggleAttribute('data-zoomed', this.isZoomed);
    this.dispatchEvent(
      new CustomEvent('drag-zoom:change', {
        bubbles: true,
        detail: { scale: this.#scale, zoomed: this.isZoomed },
      })
    );
  }

  /**
   * @returns {{ maxTranslateX: number, maxTranslateY: number } | null}
   */
  #getPanBounds() {
    if (!this.#image) return null;

    const wrapperRect = this.getBoundingClientRect();
    if (!wrapperRect.width || !wrapperRect.height) return null;

    const imageElement = this.#image;
    let naturalWidth = imageElement.naturalWidth;
    let naturalHeight = imageElement.naturalHeight;

    if (!naturalWidth || !naturalHeight) {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.height;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth;
    let actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;
    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    return {
      maxTranslateX: horizontalOverflow / 2 / this.#scale,
      maxTranslateY: verticalOverflow / 2 / this.#scale,
    };
  }

  #constrainTranslation() {
    if (!this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const bounds = this.#getPanBounds();
    if (!bounds) return;

    this.#translate.x = clamp(this.#translate.x, -bounds.maxTranslateX, bounds.maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -bounds.maxTranslateY, bounds.maxTranslateY);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to fit (1x), centered. Called when changing slides or closing.
   */
  resetZoom() {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#pointerActive = false;
    this.#activePointerId = null;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#panMode = 'mousemove';

    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.#syncZoomAttribute();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * @param {Point | Touch} point1
 * @param {Point | Touch} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */
